import { percentLabel, processingRateLabel, type StoreProvider } from "@hobbyshopify/backend-client";
import { useObserver, useSubtree } from "subtree.js/react";
import { FormError, OnboardingLayout, PrimaryButton } from "../../components/onboarding/OnboardingLayout";
import { providerMessage, providerStatusClass, providerStatusLabel } from "../../core/primitives/provider-status";
import { OnboardingPaymentsActions, OnboardingPaymentsState } from "./onboarding-payments-model";

/**
 * What the card says under the provider row, and what the primary button does.
 *
 * Stripe onboarding is a handoff that can come back in several shapes, and each
 * one needs its own sentence and its own button — "pending" is not an error, and
 * most of them are not fixed by pressing the same button again. Which of them it
 * is comes from the server as `merchantAction`: the counts behind it are Stripe's
 * and the browser never sees them.
 *
 * The case this exists for: while Stripe verifies what was already submitted,
 * there is nothing left to collect, so a hosted onboarding link opens and
 * bounces the merchant straight back here. That reads as a button that does
 * nothing, so in that state there is no such button.
 */
type PaymentsCta = { kind: "connect" | "recheck" | "dashboard"; label: string };

function stateCopy(connection: StoreProvider | null): {
  explainer: string;
  /** Null once there is nothing left to do — the footer then offers the way out. */
  cta: PaymentsCta | null;
} {
  if (connection?.status === "ACTIVE") {
    return {
      explainer: "Stripe is ready. You can take card payments and receive payouts.",
      cta: null,
    };
  }
  switch (connection?.merchantAction) {
    case "WAIT":
      return {
        explainer:
          providerMessage(connection, "Stripe") ??
          "Stripe is checking what you submitted. That can take a while, and there is nothing more for you to fill in.",
        cta: { kind: "recheck", label: "Check with Stripe" },
      };
    case "SUPPORT":
      return {
        explainer:
          providerMessage(connection, "Stripe") ??
          "Stripe will not enable this account. Only Stripe can say why, and only Stripe can lift it.",
        cta: { kind: "dashboard", label: "Open Stripe" },
      };
    default:
      return {
        // The derived sentence first wherever there is one — `lastError` is a
        // log line, not merchant copy.
        explainer:
          providerMessage(connection, "Stripe") ??
          (connection?.externalAccountRef
            ? "Stripe still needs information from you before it can enable the account."
            : "Stripe will ask for your business details and bank account. It takes a few minutes; verification can take longer."),
        cta: {
          kind: "connect",
          label: connection?.externalAccountRef ? "Continue onboarding" : "Connect Stripe",
        },
      };
  }
}

export function OnboardingPaymentsPage() {
  const state = useSubtree(OnboardingPaymentsState);
  const actions = useSubtree(OnboardingPaymentsActions);
  const observer = useObserver();
  const storeId = observer.watch(state.storeId) ?? "";
  const connection = observer.watch(state.connection);
  const providersLoading = observer.watch(state.providersLoading);
  const fees = observer.watch(state.fees);
  const connectError = observer.watch(state.error);
  const busy = observer.watch(state.busy);
  const sellsAtShows = observer.watch(state.sellsAtShows);

  const isConnected = connection?.status === "ACTIVE";
  const { explainer, cta } = stateCopy(connection);

  const runCta = () => {
    if (!cta) return;
    if (cta.kind === "connect") return void actions.connect();
    if (cta.kind === "recheck") return void actions.recheck();
    return void actions.openDashboard();
  };

  return (
    <OnboardingLayout
      step="payments"
      title="Get paid"
      description="Connect Stripe so you can take card payments and get paid out."
      storeId={storeId}
      footer={
        <>
          {cta ? (
            <PrimaryButton type="button" onClick={runCta} disabled={busy} className="w-full">
              {busy ? "One moment…" : cta.label}
            </PrimaryButton>
          ) : (
            <PrimaryButton type="button" onClick={actions.finish} className="w-full">
              Go to dashboard
            </PrimaryButton>
          )}

          {!isConnected && (
            <>
              <div className="mt-3.5 text-center">
                <button
                  type="button"
                  onClick={actions.finish}
                  className="border-b border-line pb-px text-xs text-muted transition-colors hover:text-white"
                >
                  I'll do this later
                </button>
              </div>
              <p className="mt-2.5 text-pretty text-center text-[11.5px] leading-relaxed text-faint">
                You can scan cards without Stripe. We'll remind you on your dashboard.
              </p>
            </>
          )}
        </>
      }
    >
      <FormError message={connectError} />

      <div className="rounded-row border border-line p-4">
        <div className="mb-3 flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-btn border border-line bg-surface font-mono text-xs font-semibold text-muted">
            S
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-medium text-white">Stripe</p>
            <p className="text-xs text-muted">Card payments &amp; payouts</p>
          </div>
          {providersLoading ? (
            <span className="h-[19px] w-24 animate-pulse rounded-full bg-surface-strong" />
          ) : (
            <span className={`badge shrink-0 ${providerStatusClass(connection)}`}>
              {providerStatusLabel(connection)}
            </span>
          )}
        </div>
        <p className="border-t border-line pt-3 text-xs leading-relaxed text-muted">{explainer}</p>

        {/* Shown in every state, including ACTIVE: this is a standing fact about
            what the merchant pays, not a step they can finish. It sits on the
            card next to the connect button rather than in the fine print,
            because it is the cost of the decision being made right here. */}
        <p className="mt-3 border-t border-line pt-3 text-xs leading-relaxed text-muted">
          <span className="font-medium text-white">You pay no fees on card sales.</span> Your customer pays Stripe's
          card processing fee
          {fees ? ` (${processingRateLabel(fees)})` : ""} and a HobbyScan technology fee
          {fees ? ` (${percentLabel(fees.technologyFeeBps)})` : ""} on top of the price, both shown to them at checkout,
          and you net the full price plus tax. Payouts go directly to your bank account.
        </p>
      </div>

      {sellsAtShows && (
        <p className="mt-3 text-pretty text-xs leading-relaxed text-muted">
          <span className="font-medium text-white">Have a shop as well?</span> Add its address under Settings →
          Locations any time to use the point of sale there too.
        </p>
      )}
    </OnboardingLayout>
  );
}
